<template>
	<v-card class="emissions-container" flat>
		<v-card-title class="text-center font-weight-bold text-h4 pt-4">
			Total Emissions
		</v-card-title>

		<v-card-text>
			<v-row  class="d-flex justify-center align-center">

				<v-col
					v-for="(dataset, index) in formattedData"
					:key="index"
					cols="12"
					md="4"
				>
					<v-card class="chart-box" elevation="2">
						<v-card-title class="justify-center py-2">
							{{ dataset.year }}
						</v-card-title>
						<p>Total Emission - {{ formatTotal(index) }} Kg of Co2 </p>
						<v-card-text class="py-2">
							<div class="chart-container">
								<canvas :ref="'chart' + index"></canvas>
							</div>
						</v-card-text>
					</v-card>
				</v-col>
			</v-row>

			<v-row class="mt-4">
				<v-col cols="12">
					<div class="legend">
						<div
							v-for="(label, index) in chartData.labels"
							:key="label"
							class="legend-item"
						>
							<div
								class="color-box"
								:style="{ backgroundColor: chartData.backgroundColor[index] }"
							></div>
							<span class="legend-label">{{ label }}</span>
						</div>
					</div>
				</v-col>
			</v-row>
		</v-card-text>
	</v-card>
</template>

<script>
import Chart from "chart.js"
import ChartDataLabels from "chartjs-plugin-datalabels"

Chart.plugins.register(ChartDataLabels)

export default {
	name: "EmissionsChart",
	props: {
		chartData: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			charts: [],
		}
	},
	computed: {
		formattedData() {
			// First create the data as before
			const data = this.chartData.datasets[0].labels
				.map((year, idx) => ({
					year,
					data: this.chartData.datasets[0].data[idx],
				}))

			// No need to reverse here since we want 2 to 0 order
			return data
		},
		
	},
	mounted() {
		this.$nextTick(() => {
			this.createCharts()
		})
	},
	methods: {
		formatTotal(index) {
			if (this.chartData.datasets[0].total &&
				this.chartData.datasets[0].total[index] !== undefined) {

				return new Intl.NumberFormat().format(
					Math.round(this.chartData.datasets[0].total[index])
				)
			}
			return "N/A"
		},
		createCharts() {
			this.charts.forEach((chart) => chart.destroy())
			this.charts = []

			this.formattedData.forEach((dataset, index) => {
				const ctx = this.$refs["chart" + index][0].getContext("2d")

				this.charts.push(
					new Chart(ctx, {
						type: "pie",
						plugins: [ChartDataLabels],
						data: {
							labels: this.chartData.labels,
							datasets: [
								{
									data: dataset.data,
									backgroundColor: this.chartData.backgroundColor,
									borderWidth: 0,
								},
							],
						},
						options: {
							responsive: true,
							maintainAspectRatio: false,
							layout: {
								padding: {
									top: 10,
									bottom: 10,
									left: 10,
									right: 10,
								},
							},
							legend: {
								display: false,
							},
							plugins: {
								datalabels: {
									color: "#fff",
									font: {
										weight: "bold",
										size: 16,
									},
									formatter: (value) => {
										return value + "%"
									},
									display: function (context) {
										return context.dataset.data[context.dataIndex] > 5
									},
									align: function (context) {
										const index = context.dataIndex
										const value = context.dataset.data[index]
										const sum = context.dataset.data.reduce((a, b) => a + b, 0)
										const rotation = (value / sum) * 360
										return rotation < 180 ? "end" : "start"
									},
									rotation: 0,
									distance: 0.5, // This is the key property for v0.7.0 - values between 0 and 1
								},
							},
						},
					})
				)
			})
		},
	},
	watch: {
		chartData: {
			deep: true,
			handler() {
				this.$nextTick(() => {
					this.createCharts()
				})
			},
		},
	},
}
</script>

<style scoped>
.emissions-container {
	margin: 0;
	background: transparent !important;
	box-shadow: none !important;
}

.chart-container {
	width: 100%;
	height: 280px; /* Reduced height */
	position: relative;
}

.legend {
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	gap: 16px;
	margin-top: 16px;
}
.legend-item {
	display: flex;
	align-items: center;
	gap: 8px;
}

.color-box {
	width: 16px;
	height: 16px;
	border-radius: 3px;
}

.legend-label {
	font-size: 14px;
	color: rgba(0, 0, 0, 0.87);
}
/* Reduce card padding */
:deep(.v-card-text) {
	padding: 8px 16px;
}

/* Adjust title spacing */
:deep(.v-card-title) {
	padding: 8px 16px;
	font-size: 1.25rem;
}
</style>
